/**
 * Supported currencies and the one fact about each that money arithmetic needs:
 * how many minor-unit digits it has (ADR 060 — Money Type § 2.1, § 2.7).
 */

/** ISO 4217 currency codes officially supported by the platform. */
export const SUPPORTED_CURRENCIES = [
    'USD',
    'EUR',
    'GBP',
    'CAD',
    'AUD',
    'NZD',
    'CHF',
    'JPY',
    'SEK',
    'NOK',
    'DKK',
] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

/** Display labels for the tenant currency picker. */
export const CURRENCY_LABELS: Record<SupportedCurrency, string> = {
    USD: 'US Dollar',
    EUR: 'Euro',
    GBP: 'British Pound',
    CAD: 'Canadian Dollar',
    AUD: 'Australian Dollar',
    NZD: 'New Zealand Dollar',
    CHF: 'Swiss Franc',
    JPY: 'Japanese Yen',
    SEK: 'Swedish Krona',
    NOK: 'Norwegian Krone',
    DKK: 'Danish Krone',
};

/**
 * A currency's plural name in running English ("US dollars", "Japanese yen",
 * "Swedish kronor"), for copy that says what amounts are in. Read off ICU's
 * own currency names rather than a second per-currency table, so irregular
 * plurals are right (BL-2371).
 *
 * @param currency - A supported ISO 4217 code.
 * @returns The plural name.
 */
export function currencyPluralName(currency: SupportedCurrency): string {
    const cached = PLURAL_NAMES.get(currency);
    if (cached != null) return cached;
    const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'name' }).formatToParts(2);
    const name = parts.find((part) => part.type === 'currency')?.value ?? currency;
    PLURAL_NAMES.set(currency, name);
    return name;
}

// Each name is constant per code; building an Intl formatter is the expensive part.
const PLURAL_NAMES = new Map<SupportedCurrency, string>();

/**
 * Narrows an unknown value to a {@link SupportedCurrency}.
 *
 * @param c - The value to test.
 * @returns True when `c` is a 3-letter ISO code we officially support.
 */
export function isSupportedCurrency(c: unknown): c is SupportedCurrency {
    return (
        typeof c === 'string' &&
        (SUPPORTED_CURRENCIES as readonly string[]).includes(c)
    );
}

/**
 * ISO 4217 minor-unit digits per supported currency. The exponent is a property
 * of the CURRENCY, never of a column name or a column's scale (ADR 060 § 2.1) —
 * this table is the only place it is written down. JPY has none, which is why
 * nothing may assume ×100 on the way to a processor.
 */
const MINOR_UNITS: Record<SupportedCurrency, number> = {
    USD: 2,
    EUR: 2,
    GBP: 2,
    CAD: 2,
    AUD: 2,
    NZD: 2,
    CHF: 2,
    JPY: 0,
    SEK: 2,
    NOK: 2,
    DKK: 2,
};

/**
 * The number of minor-unit digits a currency is rounded and displayed to.
 *
 * @param currency - A supported ISO 4217 code.
 * @returns 0 for JPY, 2 for every other supported currency.
 */
export function minorUnits(currency: SupportedCurrency): number {
    return MINOR_UNITS[currency];
}
